import { useMemo } from 'react';

import { useSettingsQuery } from './useSettingsQuery.js';

/**
 * The workspace defaults a NEW invoice or estimate starts from, read off the same coarse
 * `GET /api/v1/settings` the Settings page uses (one cache entry, `settingsQueryKey`).
 *
 * This is a convenience read, never a gate: a Manager may lack `settings.view`, in which case
 * the call 403s and the forms simply keep their blank defaults. Callers get `undefined` until
 * the read lands and forever if it fails, so a slow or refused fetch never blocks a form or
 * shows an error. Only create forms should pass `enabled`; an existing record carries its own
 * values and must never be overwritten by a workspace default.
 */
export interface SettingsDefaults {
  defaultTaxRate?: number;
  proposalDefaultTerms?: string;
  /** Payment terms in days: the due date a new invoice is given, as the server applies it. */
  invoiceTermsDays?: number;
}

export function useSettingsDefaults(enabled: boolean): SettingsDefaults | undefined {
  // staleTime 0 forces a refetch on every mount, and the cached copy is withheld until that
  // refetch settles: the consuming effect fills ONCE, so handing it a stale snapshot first would
  // lock in an old rate. A failed refetch keeps the stale data but stops fetching, so it is used.
  const query = useSettingsQuery({ enabled, staleTime: 0 });
  const data = query.isFetching ? undefined : query.data;
  const settings = data?.settings;
  const termsDays = data?.companySettings?.invoiceTermsDays;
  // Memoised so the consuming effect runs once per server response, not once per render.
  return useMemo(() => {
    if (!settings) return undefined;
    return {
      defaultTaxRate:
        typeof settings.defaultTaxRate === 'number' ? settings.defaultTaxRate : undefined,
      proposalDefaultTerms:
        typeof settings.proposalDefaultTerms === 'string'
          ? settings.proposalDefaultTerms
          : undefined,
      invoiceTermsDays: typeof termsDays === 'number' ? termsDays : undefined,
    };
  }, [settings, termsDays]);
}

/**
 * The Settings page stores proposal terms as plain text (a `<textarea>`); the estimate's terms
 * field is a rich-text editor that stores HTML. Handed plain text, the editor parses it as HTML
 * and collapses every line break into a space, so multi-line terms would arrive as one run-on
 * paragraph. Blank-line-separated blocks become `<p>`, single newlines become `<br>`, and
 * anything that already looks like markup is passed through untouched.
 */
export function termsTextToHtml(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return '';
  if (/<[a-z][^>]*>/i.test(trimmed)) return trimmed;
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return trimmed
    .split(/\n\s*\n/)
    .map(
      (block) =>
        `<p>${block
          .split('\n')
          .map((line) => escapeHtml(line.trim()))
          .join('<br>')}</p>`,
    )
    .join('');
}
